import React from 'react';
import { Zap, BarChart3, Smartphone, Heart, MessageCircle, Repeat2, Hash } from 'lucide-react';
import { reverseText, mirrorText, spaceText, titleCase } from '../../lib/unicode';

export function PlatformMock({ platform, text, sample }) {
  const shown = text || sample;
  if (platform === 'Instagram') {
    return (
      <div className="rounded-lg bg-white border border-[var(--line)] p-2.5 flex gap-2.5 items-start" data-testid="preview-instagram">
        <div className="w-8 h-8 rounded-full shrink-0 p-[2px] bg-[conic-gradient(#f9ce34,#ee2a7b,#6228d7,#f9ce34)]"><div className="w-full h-full rounded-full bg-[var(--paper2)] border-2 border-white" /></div>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold text-[var(--ink)]">yourname</div>
          <div className="text-[12.5px] text-[var(--ink2)] break-words">{shown}</div>
        </div>
      </div>
    );
  }
  if (platform === 'Twitter') {
    return (
      <div className="rounded-lg bg-white border border-[var(--line)] p-2.5" data-testid="preview-twitter">
        <div className="flex items-center gap-2 mb-1"><div className="w-6 h-6 rounded-full bg-slate-300" /><span className="text-[11px] font-bold text-slate-800">You</span><span className="text-[10px] text-slate-400">@you · 1m</span></div>
        <div className="text-[12.5px] text-slate-800 break-words">{shown}</div>
        <div className="flex gap-5 mt-1.5 text-slate-400"><MessageCircle className="w-3 h-3" /><Repeat2 className="w-3 h-3" /><Heart className="w-3 h-3" /></div>
      </div>
    );
  }
  if (platform === 'TikTok') {
    return (
      <div className="rounded-lg bg-[#121212] p-2.5 text-white" data-testid="preview-tiktok">
        <div className="text-[11px] font-semibold">@yourname</div>
        <div className="text-[12.5px] break-words opacity-95">{shown}</div>
        <div className="text-[10px] text-[#25f4ee] mt-1">♫ original sound</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg bg-[#313338] p-2.5 text-white flex gap-2" data-testid="preview-discord">
      <div className="w-7 h-7 rounded-full bg-[#5865f2] shrink-0 flex items-center justify-center"><Hash className="w-3.5 h-3.5" /></div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold text-[#f2f3f5]">yourname <span className="text-[9px] text-[#949ba4] font-normal">Today at 12:00</span></div>
        <div className="text-[12.5px] text-[#dbdee1] break-words">{shown}</div>
      </div>
    </div>
  );
}

export default function ToolExtras({ t, input, setInput, output, stats, platform, setPlatform }) {
  const transforms = [
    ['UPPER', (s) => s.toUpperCase()],
    ['lower', (s) => s.toLowerCase()],
    ['Title', titleCase],
    ['Reverse', reverseText],
    ['S p a c e', spaceText],
    ['Mirror', mirrorText],
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="tool-extras">
      <div className="tool-card px-3.5 py-3.5">
        <div className="upper-label flex items-center gap-1.5 mb-3"><Zap className="w-3.5 h-3.5 text-[var(--gold2)]" />{t.quick}</div>
        <div className="flex flex-wrap gap-1.5">
          {transforms.map(([label, fn]) => (
            <button key={label} className="pill-btn" onClick={() => setInput(fn(input))} data-testid={`transform-${label.replace(/\s/g, '').toLowerCase()}`}>{label}</button>
          ))}
        </div>
      </div>
      <div className="tool-card px-3.5 py-3.5">
        <div className="upper-label flex items-center gap-1.5 mb-3"><BarChart3 className="w-3.5 h-3.5 text-[var(--gold2)]" />{t.stats}</div>
        <div className="grid grid-cols-2 gap-2">
          {[[stats.chars, t.characters], [stats.words, t.words], [stats.sentences, t.sentences], [stats.letters, t.letters]].map(([v, l], i) => (
            <div key={i} className="stat-box">
              <div className="stat-val" data-testid={`stat-${i}`}>{v}</div>
              <div className="upper-label !text-[9px] mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="tool-card px-3.5 py-3.5">
        <div className="upper-label flex items-center gap-1.5 mb-3"><Smartphone className="w-3.5 h-3.5 text-[var(--gold2)]" />{t.platform}</div>
        <div className="flex flex-wrap gap-1.5 mb-2.5">
          {['Instagram', 'Twitter', 'TikTok', 'Discord'].map((p) => (
            <button key={p} className={`pill-btn ${platform === p ? 'active' : ''}`} onClick={() => setPlatform(p)} data-testid={`platform-${p.toLowerCase()}`}>{p}</button>
          ))}
        </div>
        <PlatformMock platform={platform} text={output} sample={t.outPlaceholder} />
        <div className="text-[10.5px] text-[var(--ink4)] mt-2">{t.preview}: {platform}</div>
      </div>
    </div>
  );
}
